<template>
  <div class="situation-photos" :class="{ 'situation-photos--compact': compact }">
    <div v-if="groupedPhotos.general.length || groupedPhotos.products.length || editable" class="situation-photos__content">
      <!-- General photos -->
      <template v-if="groupedPhotos.general.length">
        <p v-if="hasProductPhotos" class="situation-photos__group-label">Algemene instructies</p>
        <div class="situation-photos__grid">
          <div
            v-for="photo in groupedPhotos.general"
            :key="photo.id"
            class="situation-photos__item"
          >
            <div class="situation-photos__thumb" @click="openPreview(photo)">
              <img :src="photo.url" :alt="photo.description || 'Situatiefoto'" />
              <button
                v-if="editable"
                class="situation-photos__remove"
                @click.stop="$emit('remove', photo.id)"
                type="button"
              >×</button>
            </div>
            <div class="situation-photos__desc" v-if="photo.description || editable">
              <input
                v-if="editable"
                type="text"
                :value="photo.description"
                @change="$emit('update-description', photo.id, $event.target.value)"
                placeholder="Beschrijving / instructie..."
                class="situation-photos__desc-input"
              />
              <span v-else class="situation-photos__desc-text">{{ photo.description }}</span>
            </div>
          </div>
        </div>
      </template>

      <!-- Per-product photos -->
      <template v-for="group in groupedPhotos.products" :key="group.name">
        <p class="situation-photos__group-label">{{ group.name }}</p>
        <div class="situation-photos__grid">
          <div
            v-for="photo in group.photos"
            :key="photo.id"
            class="situation-photos__item"
          >
            <div class="situation-photos__thumb" @click="openPreview(photo)">
              <img :src="photo.url" :alt="photo.description || 'Situatiefoto'" />
              <button
                v-if="editable"
                class="situation-photos__remove"
                @click.stop="$emit('remove', photo.id)"
                type="button"
              >×</button>
            </div>
            <div class="situation-photos__desc" v-if="photo.description || editable">
              <input
                v-if="editable"
                type="text"
                :value="photo.description"
                @change="$emit('update-description', photo.id, $event.target.value)"
                placeholder="Beschrijving / instructie..."
                class="situation-photos__desc-input"
              />
              <span v-else class="situation-photos__desc-text">{{ photo.description }}</span>
            </div>
          </div>
        </div>
      </template>

      <!-- Upload button -->
      <div v-if="editable" class="situation-photos__add-row">
        <label class="situation-photos__add-btn" :class="{ 'situation-photos__add-btn--uploading': uploading }">
          <BaseIcon name="plus" size="16" />
          <span>{{ uploading ? 'Uploaden...' : 'Foto toevoegen' }}</span>
          <input
            ref="fileInput"
            type="file"
            accept="image/*"
            multiple
            @change="handleFileSelect"
            style="display: none"
          />
        </label>
      </div>

      <!-- Empty state (editable) -->
      <div v-if="!photos.length && !editable" class="situation-photos__empty">
        <p>Geen situatiefoto's beschikbaar.</p>
      </div>
    </div>

    <!-- Lightbox -->
    <teleport to="body">
      <div v-if="previewPhoto" class="situation-photos-lightbox" @click.self="previewPhoto = null">
        <button class="situation-photos-lightbox__close" @click="previewPhoto = null">×</button>
        <div class="situation-photos-lightbox__content">
          <img :src="previewPhoto.url" :alt="previewPhoto.description || 'Situatiefoto'" />
          <p v-if="previewPhoto.description" class="situation-photos-lightbox__desc">
            {{ previewPhoto.description }}
          </p>
          <p v-if="previewPhoto.product_name" class="situation-photos-lightbox__product">
            {{ previewPhoto.product_name }}
          </p>
        </div>
      </div>
    </teleport>
  </div>
</template>

<script>
  import { ref, computed } from 'vue';
  import FileService from '@/services/fileService';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  export default {
    name: 'SituationPhotos',
    components: { BaseIcon },
    props: {
      photos: {
        type: Array,
        default: () => [],
      },
      editable: {
        type: Boolean,
        default: false,
      },
      compact: {
        type: Boolean,
        default: false,
      },
      uploadConfig: {
        type: Object,
        default: () => ({ type: 'situation_photo', context: 'project' }),
      },
    },
    emits: ['add', 'remove', 'update-description'],
    setup(props, { emit }) {
      const fileInput = ref(null);
      const uploading = ref(false);
      const previewPhoto = ref(null);

      const groupedPhotos = computed(() => {
        const general = [];
        const productMap = {};

        (props.photos || []).forEach((photo) => {
          if (photo.type === 'product' && photo.product_name) {
            if (!productMap[photo.product_name]) {
              productMap[photo.product_name] = [];
            }
            productMap[photo.product_name].push(photo);
          } else {
            general.push(photo);
          }
        });

        const products = Object.entries(productMap).map(([name, photos]) => ({
          name,
          photos,
        }));

        return { general, products };
      });

      const hasProductPhotos = computed(() => groupedPhotos.value.products.length > 0);

      const openPreview = (photo) => {
        previewPhoto.value = photo;
      };

      const handleFileSelect = async (event) => {
        const files = Array.from(event.target.files);
        if (!files.length) return;

        uploading.value = true;
        try {
          for (const file of files) {
            const response = await FileService.uploadFile(file, props.uploadConfig);
            if (response?.url) {
              emit('add', {
                id: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(),
                url: response.url,
                description: '',
              });
            }
          }
        } catch (error) {
          console.error('Failed to upload situation photo:', error);
        } finally {
          uploading.value = false;
          if (fileInput.value) fileInput.value.value = '';
        }
      };

      return {
        fileInput,
        uploading,
        previewPhoto,
        groupedPhotos,
        hasProductPhotos,
        openPreview,
        handleFileSelect,
      };
    },
  };
</script>

<style scoped lang="scss">
  .situation-photos {
    &__content {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
    }

    &__group-label {
      margin: 0;
      font-size: 0.6875rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      color: var(--slate-400);
    }

    &__grid {
      display: flex;
      flex-wrap: wrap;
      gap: 0.625rem;
    }

    &__item {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      width: 5.5rem;
    }

    &--compact &__item {
      width: 4.5rem;
    }

    &__thumb {
      position: relative;
      width: 100%;
      aspect-ratio: 1;
      border-radius: 0.375rem;
      overflow: hidden;
      border: 1px solid var(--slate-200);
      cursor: pointer;
      transition: border-color 0.15s ease;

      @include hover {
        border-color: var(--brand-400);
      }

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    &__remove {
      position: absolute;
      top: 2px;
      right: 2px;
      width: 1.125rem;
      height: 1.125rem;
      background: rgba(0, 0, 0, 0.55);
      color: white;
      border: none;
      border-radius: 50%;
      font-size: 0.7rem;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    &__desc-text {
      font-size: 0.6875rem;
      color: var(--slate-600);
      line-height: 1.3;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    &__desc-input {
      width: 100%;
      font-size: 0.6875rem;
      border: 1px solid var(--slate-200);
      border-radius: 0.25rem;
      padding: 0.125rem 0.25rem;
      color: var(--slate-700);
      outline: none;
      transition: border-color 0.15s ease;

      &:focus {
        border-color: var(--brand-400);
      }

      &::placeholder {
        color: var(--slate-300);
      }
    }

    &__add-row {
      margin-top: 0.25rem;
    }

    &__add-btn {
      display: inline-flex;
      align-items: center;
      gap: 0.375rem;
      padding: 0.375rem 0.75rem;
      border: 1.5px dashed var(--slate-300);
      border-radius: 0.375rem;
      color: var(--slate-500);
      font-size: 0.8125rem;
      cursor: pointer;
      transition: all 0.15s ease;
      background: none;

      @include hover {
        border-color: var(--brand-400);
        color: var(--brand-600);
        background: var(--brand-50);
      }

      &--uploading {
        opacity: 0.6;
        pointer-events: none;
      }
    }

    &__empty {
      padding: 0.75rem 0;
      color: var(--slate-400);
      font-size: 0.8125rem;

      p {
        margin: 0;
      }
    }
  }

  .situation-photos-lightbox {
    position: fixed;
    inset: 0;
    z-index: 9999;
    background: rgba(0, 0, 0, 0.85);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;

    &__close {
      position: absolute;
      top: 1rem;
      right: 1rem;
      width: 2.5rem;
      height: 2.5rem;
      border: none;
      background: rgba(255, 255, 255, 0.15);
      color: white;
      font-size: 1.5rem;
      border-radius: 50%;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.15s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.25);
      }
    }

    &__content {
      max-width: 90vw;
      max-height: 85vh;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.75rem;

      img {
        max-width: 100%;
        max-height: 75vh;
        border-radius: 0.5rem;
        object-fit: contain;
      }
    }

    &__desc {
      color: white;
      font-size: 0.9375rem;
      text-align: center;
      margin: 0;
      max-width: 600px;
    }

    &__product {
      color: rgba(255, 255, 255, 0.6);
      font-size: 0.8125rem;
      margin: 0;
    }
  }
</style>
